import type { StartingCombatContent } from '../content/types.ts';
import type { CrawlerType } from '../domain/character.ts';
import type { StartingSkill } from './backgrounds.ts';

/**
 * Starting combat Skills.
 *
 * RULE DCC-COMBAT-START-001 - Crawlers & Customization p.12.
 * Every crawler gets an automatic first combat Skill at Rank 3: Unarmed Combat
 * for humans, Slice Attack for animals.
 *
 * RULE DCC-COMBAT-START-002 - C&C pp.12-13.
 * The second combat Skill, also Rank 3, is one of:
 *   - a weapon from the printed list;
 *   - one of five low-Mana Attack Spells;
 *   - a hand-to-hand Skill, which comes paired with a Damage Effect.
 *
 * RULE DCC-COMBAT-START-003 - C&C p.13.
 * Taking an Attack Spell requires setting Intelligence to 4 or higher, and
 * grants 5 Standard Mana Potions.
 *
 * RULE DCC-COMBAT-START-004 - C&C p.15.
 * Every crawler starts with the Heal Spell at Rank 1 (Rank 1 maximum).
 */

export type SecondCombatChoice =
  | { kind: 'weapon'; skillId: string }
  | { kind: 'spell'; skillId: string }
  | { kind: 'hand-to-hand'; skillId: string };

export interface CombatSelection {
  second: SecondCombatChoice | null;
}

export interface CombatProblem {
  message: string;
  /** Which earlier creation step the player must return to, if any. */
  fixAtStep?: 'stats';
}

export interface CombatValidation {
  valid: boolean;
  problems: CombatProblem[];
}

export function automaticCombatSkillId(
  content: StartingCombatContent,
  crawlerType: CrawlerType,
): string | null {
  const entry = content.automatic.find((a) => a.crawlerType === crawlerType);
  return entry ? entry.skillId : null;
}

/** RULE DCC-COMBAT-START-003 - checked before the choice is accepted. */
export function meetsAttackSpellPrerequisite(
  content: StartingCombatContent,
  intelligence: number,
): boolean {
  return intelligence >= content.attackSpellMinimumIntelligence;
}

export function validateCombatSelection(
  selection: CombatSelection,
  content: StartingCombatContent,
  context: { intelligence: number; takenSkillIds: ReadonlySet<string> },
): CombatValidation {
  const problems: CombatProblem[] = [];
  const choice = selection.second;

  if (!choice) {
    return { valid: false, problems: [{ message: 'Choose a second combat Skill.' }] };
  }

  if (choice.kind === 'weapon' && !content.weaponSkillIds.includes(choice.skillId)) {
    problems.push({ message: `"${choice.skillId}" is not on the starting weapon list (C&C p.12).` });
  }
  if (choice.kind === 'spell') {
    if (!content.attackSpellIds.includes(choice.skillId)) {
      problems.push({ message: `"${choice.skillId}" is not a starting Attack Spell (C&C p.13).` });
    }
    if (!meetsAttackSpellPrerequisite(content, context.intelligence)) {
      problems.push({
        message: `A starting Attack Spell requires Intelligence ${content.attackSpellMinimumIntelligence} or higher (C&C p.13). This crawler has ${context.intelligence}.`,
        fixAtStep: 'stats',
      });
    }
  }
  if (choice.kind === 'hand-to-hand' && !content.handToHand.some((h) => h.skillId === choice.skillId)) {
    problems.push({ message: `"${choice.skillId}" is not a starting hand-to-hand Skill (C&C p.13).` });
  }

  // RULE DCC-BG-003 also applies across background and combat Skills.
  if (context.takenSkillIds.has(choice.skillId)) {
    problems.push({
      message: `"${choice.skillId}" was already taken from a background. Ranks from different sources are not added together (C&C p.6).`,
    });
  }

  return { valid: problems.length === 0, problems };
}

export interface StartingCombatResult {
  skills: StartingSkill[];
  /** Extra items the rules grant alongside the choice, recorded as notes. */
  grants: string[];
  /** The same grants as Items, for the crawler's inventory. */
  grantItems: { itemId: string; quantity: number }[];
}

export function startingCombatSkills(
  selection: CombatSelection,
  content: StartingCombatContent,
  crawlerType: CrawlerType,
): StartingCombatResult {
  const skills: StartingSkill[] = [];
  const grants: string[] = [];
  const grantItems: { itemId: string; quantity: number }[] = [];

  const automatic = content.automatic.find((a) => a.crawlerType === crawlerType);
  if (automatic) {
    skills.push({ skillId: automatic.skillId, rank: automatic.rank, origin: 'Combat Background' });
  }

  const choice = selection.second;
  if (choice) {
    skills.push({ skillId: choice.skillId, rank: content.secondSkillRank, origin: 'Second combat Skill' });

    if (choice.kind === 'spell') {
      grants.push(content.attackSpellGrant);
      grantItems.push(...content.attackSpellGrantItems);
    }
    if (choice.kind === 'hand-to-hand') {
      const pair = content.handToHand.find((h) => h.skillId === choice.skillId);
      if (pair) {
        // The Damage Effect comes with the Skill. C&C p.13 awards Rank 3 to the
        // second combat background Skill; the Damage Effect is an ADDITIONAL
        // Passive Skill (C&C pp.24-25) with no stated Rank, so it is recorded at
        // Rank 1, the lowest recordable Rank (C&C p.6).
        // See docs/RULES_CONFLICTS.md RESOLVED-004. This is an interpretation.
        skills.push({
          skillId: pair.damageEffectSkillId,
          rank: 1,
          origin: 'Hand-to-Hand Damage Effect',
        });
      }
      grants.push(content.handToHandGrant);
      grantItems.push(...content.handToHandGrantItems);
    }
  }

  // RULE DCC-COMBAT-START-004 - the Heal Spell, for every crawler.
  for (const spellId of content.startingSpellIds) {
    skills.push({ skillId: spellId, rank: content.startingSpellRank, origin: 'Starting Spell' });
  }

  return { skills, grants, grantItems };
}

/**
 * Merges every starting Skill source into the final Skill list.
 * Duplicates should already have been rejected by validation; if one survives,
 * the highest Rank wins rather than the Ranks being added (C&C p.6).
 */
export function mergeStartingSkills(
  groups: readonly (readonly StartingSkill[])[],
): { skillId: string; rank: number }[] {
  const best = new Map<string, number>();
  for (const group of groups) {
    for (const entry of group) {
      const current = best.get(entry.skillId);
      if (current === undefined || entry.rank > current) best.set(entry.skillId, entry.rank);
    }
  }
  return [...best.entries()]
    .map(([skillId, rank]) => ({ skillId, rank }))
    .sort((a, b) => a.skillId.localeCompare(b.skillId));
}
